import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, ShieldCheck, Clock, MapPin, Star, PhoneCall, Wallet, Car, Sparkles } from "lucide-react";
import VehicleCard from "@/components/VehicleCard";

const testimonials = [
  { name: "Marcus H.", role: "CEO, Northbridge Capital", quote: "The S-Class was spotless and delivered on time. Best rental experience I've had in Texas.", img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200" },
  { name: "Elena V.", role: "Wedding planner", quote: "Global Prime Fleet made our couple's day flawless. The chauffeur was courteous and the car was pristine.", img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200" },
  { name: "Daniel R.", role: "Travel blogger", quote: "Booked their Range Rover for a road trip — the process was seamless and the vehicle immaculate.", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200" },
];

const features = [
  { icon: Wallet, title: "Affordable Pricing", body: "Transparent, all-inclusive rates with no hidden fees." },
  { icon: Sparkles, title: "Luxury Fleet", body: "Late-model premium vehicles serviced to concierge standards." },
  { icon: PhoneCall, title: "24/7 Support", body: "Real humans, day or night, ready to help wherever you are." },
  { icon: ShieldCheck, title: "Fully Insured", body: "Every rental covered with comprehensive protection plans." },
  { icon: Car, title: "Roadside Assistance", body: "Complimentary roadside cover on every reservation." },
  { icon: Clock, title: "Flexible Plans", body: "Daily, weekly, monthly and long-term leasing options." },
];

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ pickup: "Plano, TX", dropoff: "Plano, TX", pickup_date: "", return_date: "" });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1764605206511-7a649d9df63b?w=1800" alt=""
            className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#040B16] via-[#040B16]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#040B16] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow mb-6">Drive Excellence. Anywhere. Anytime.</div>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Premium Car Rentals<br />
              <span className="text-[#D4AF37]">for Every Journey.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
              Whether you're traveling for business, leisure, or a special occasion, Global Prime Fleet delivers reliable, affordable, and luxury vehicles tailored to your needs.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/fleet" data-testid="hero-book-now" className="btn-gold">Book Now <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/services" data-testid="hero-explore" className="btn-ghost">Explore Services</Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs tracking-widest uppercase text-white/50">
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-[#D4AF37]" /> 4.9 avg. rating</div>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative max-w-7xl mx-auto px-6 -mt-16 pb-16">
          <div className="bg-white text-[#0B1F3A] rounded-2xl shadow-2xl border border-black/5 p-6 grid md:grid-cols-5 gap-4">
            <Field label="Pick-up location" testId="search-pickup" value={form.pickup} onChange={(v) => setForm({ ...form, pickup: v })} icon={<MapPin className="w-4 h-4 text-[#D4AF37]" />} />
            <Field label="Drop-off location" testId="search-dropoff" value={form.dropoff} onChange={(v) => setForm({ ...form, dropoff: v })} icon={<MapPin className="w-4 h-4 text-[#D4AF37]" />} />
            <Field label="Pick-up date" type="datetime-local" testId="search-pickup-date" value={form.pickup_date} onChange={(v) => setForm({ ...form, pickup_date: v })} />
            <Field label="Return date" type="datetime-local" testId="search-return-date" value={form.return_date} onChange={(v) => setForm({ ...form, return_date: v })} />
            <button onClick={() => {
              const qs = new URLSearchParams();
              if (form.pickup_date) qs.set("pickup", form.pickup_date);
              if (form.return_date) qs.set("return", form.return_date);
              nav(`/fleet${qs.toString() ? "?" + qs.toString() : ""}`);
            }} data-testid="search-submit"
              className="btn-gold justify-center h-full">Search</button>
          </div>
        </div>
      </section>

      {/* FEATURED FLEET */}
      <section className="bg-[#040B16] py-24 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-3">Featured Fleet</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight max-w-2xl">A curated selection of exceptional vehicles.</h2>
            </div>
            <Link to="/fleet" data-testid="home-view-all-fleet" className="btn-ghost">View all vehicles</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-[#F5F5F5] text-[#0B1F3A] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="eyebrow mb-3" style={{ color: "#0B1F3A" }}>Why choose us</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Rental redefined for people who value time, comfort and reliability.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-[#0B1F3A]/70 self-end leading-relaxed">
              From executive travel to family road trips, we combine a spotless luxury fleet with concierge-grade service. Every reservation includes insurance, roadside assistance, and a promise we keep — on time, every time.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-white rounded-2xl border border-black/5 p-8 hover:border-[#D4AF37] hover:-translate-y-1 transition-[transform,border-color] duration-300">
                <f.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="font-display text-xl font-bold tracking-tight">{f.title}</h3>
                <p className="text-sm text-[#0B1F3A]/60 mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#040B16] py-24 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="eyebrow mb-3">Guest voices</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-12 max-w-3xl">Words from those who chose to travel differently.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="card-marketing p-8" data-testid={`testimonial-${t.name}`}>
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#D4AF37]" fill="#D4AF37" />)}</div>
                <p className="text-white/80 leading-relaxed italic">"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-6">
                  <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#040B16] pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-3xl bg-gradient-to-br from-[#112A4A] to-[#0B1F3A] border border-white/10 p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden">
            <div className="max-w-xl">
              <div className="eyebrow mb-3">Ready to drive?</div>
              <h3 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-white">Reserve your next journey in under 60 seconds.</h3>
            </div>
            <Link to="/fleet" data-testid="cta-book-now" className="btn-gold">Book a vehicle</Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, type = "text", icon, testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">{label}</span>
      <div className="flex items-center gap-2 border-b border-[#0B1F3A]/15 pb-2 focus-within:border-[#D4AF37] transition-colors">
        {icon}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="flex-1 bg-transparent outline-none text-sm text-[#0B1F3A] placeholder-[#0B1F3A]/40"
          placeholder={label}
        />
      </div>
    </label>
  );
}
